import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import logoImg from '../assets/bee-appee-logo.png';
import './Auth.css';

const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 96 96" fill="none">
    <rect x="22" y="42" width="52" height="38" rx="12" stroke="currentColor" strokeWidth="6"/>
    <path d="M34 42v-9c0-10 6-17 14-17s14 7 14 17v9" stroke="currentColor" strokeWidth="6" strokeLinecap="round"/>
    <circle cx="48" cy="61" r="5" fill="currentColor"/>
  </svg>
);

const IconEye = ({ off }) => off ? (
  <svg width="18" height="18" viewBox="0 0 96 96" fill="none">
    <path d="M12 48s13-22 36-22 36 22 36 22-13 22-36 22-36-22-36-22Z" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="48" cy="48" r="11" stroke="currentColor" strokeWidth="6"/>
    <line x1="16" y1="16" x2="80" y2="80" stroke="currentColor" strokeWidth="6" strokeLinecap="round"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 96 96" fill="none">
    <path d="M12 48s13-22 36-22 36 22 36 22-13 22-36 22-36-22-36-22Z" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="48" cy="48" r="11" stroke="currentColor" strokeWidth="6"/>
  </svg>
);

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const email = params.get('email') || '';

  const [password, setPassword]       = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [showPwd2, setShowPwd2]       = useState(false);
  const [confirm, setConfirm]         = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState(false);

  // Token absent = lien invalide
  const invalidLink = !token || !email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true); setError('');
    try {
      await api.resetPassword(token, email, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message || 'Lien invalide ou expiré. Faites une nouvelle demande.');
    } finally {
      setLoading(false);
    }
  };

  const strength = password.length === 0 ? 0
    : password.length < 6 ? 1
    : password.length < 10 ? 2
    : /[A-Z]/.test(password) && /[0-9]/.test(password) ? 4
    : 3;
  const strengthLabel = ['', 'Trop court', 'Moyen', 'Bien', 'Excellent !'];
  const strengthColor = ['', '#EF4444', '#F59E0B', '#10B981', '#10B981'];

  return (
    <div className="rp-page">
      <div className="rp-card">
        <img src={logoImg} alt="bee appee" className="rp-logo" />

        {invalidLink ? (
          <div className="rp-invalid">
            <div className="rp-icon">⚠️</div>
            <h2 className="rp-title">Lien invalide</h2>
            <p className="rp-sub">Ce lien de réinitialisation est invalide ou a déjà été utilisé.</p>
            <Link to="/login" className="rp-link-btn">← Retour à la connexion</Link>
          </div>
        ) : success ? (
          <div className="rp-success">
            <div className="rp-icon">✅</div>
            <h2 className="rp-title">Mot de passe mis à jour !</h2>
            <p className="rp-sub">
              Votre mot de passe a été réinitialisé avec succès.<br />
              Vous allez être redirigé vers la connexion…
            </p>
            <Link to="/login" className="rp-link-btn">Se connecter maintenant →</Link>
          </div>
        ) : (
          <>
            <div className="rp-icon">🔑</div>
            <h2 className="rp-title">Nouveau mot de passe</h2>
            <p className="rp-sub">Choisissez un mot de passe sécurisé pour <strong>{email}</strong>.</p>

            {error && <div className="rp-error">⚠️ {error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="rp-field">
                <label className="rp-label">Nouveau mot de passe</label>
                <div className="rp-input-wrap">
                  <span className="rp-input-icon"><IconLock /></span>
                  <input
                    className="rp-input"
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Au moins 8 caractères"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required minLength={8}
                    autoComplete="new-password"
                  />
                  <button type="button" className="rp-eye-btn" onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
                    <IconEye off={showPwd} />
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="rp-strength">
                    <div className="rp-strength-bar">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="rp-strength-seg" style={{ background: i <= strength ? strengthColor[strength] : '#E8E5F4' }} />
                      ))}
                    </div>
                    <span className="rp-strength-label" style={{ color: strengthColor[strength] }}>
                      {strengthLabel[strength]}
                    </span>
                  </div>
                )}
              </div>

              <div className="rp-field">
                <label className="rp-label">Confirmer le mot de passe</label>
                <div className="rp-input-wrap">
                  <span className="rp-input-icon"><IconLock /></span>
                  <input
                    className="rp-input"
                    type={showPwd2 ? 'text' : 'password'}
                    placeholder="Répétez votre mot de passe"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                  <button type="button" className="rp-eye-btn" onClick={() => setShowPwd2(v => !v)} tabIndex={-1}>
                    <IconEye off={showPwd2} />
                  </button>
                </div>
                {confirm.length > 0 && password !== confirm && (
                  <p className="rp-mismatch">Les mots de passe ne correspondent pas</p>
                )}
              </div>

              <button
                type="submit"
                className={`rp-btn${loading ? ' rp-btn-loading' : ''}`}
                disabled={loading || password.length < 8 || password !== confirm}
              >
                {loading ? <span className="rp-spinner" /> : 'Réinitialiser mon mot de passe →'}
              </button>
            </form>

            <Link to="/login" className="rp-back">← Retour à la connexion</Link>
          </>
        )}
      </div>
    </div>
  );
}
